import React, { useEffect, useState, useCallback, Fragment } from "react";
import { useSelector, useDispatch } from "react-redux";
import dayjs from "dayjs";
import localeData from "dayjs/plugin/localeData";
import objectSupport from "dayjs/plugin/objectSupport";

import { useReturn } from "util/return";

import {
  useAffiliationChangeInformation,
  getStatusSummary,
  AFFILIATION_CHANGEABLE_FIELDS,
} from "./common";

import {
  ShowOriginalStatus,
  useOriginalStatusDetails,
  useProposedStatusDetails,
} from "./statusDetails";
import { PromptLocation } from "components/shared/location";

import { usePrepareDatesSection, DatesSection } from "./dates";

import { changesSlice, updateChanges } from "redux/features/changes";
import { useGeographicalLocations } from "redux/features/countries";

import { useAuthenticationState } from "redux/features/auth";

import { profileSlice } from "redux/features/profile";

import useOccupationDetail from "components/shared/occupation";
import { usePhdYearState, PromptPhdYear } from "components/shared/phdYear";

import ErrorMessage from "util/errorMessage";
import { Form, Message, Content } from "react-bulma-components";

import {
  usePreloadInstitutionOptions,
  useInstitutionLookup,
  AffiliationPrompt,
} from "./institution";

import PromptOccupation from "./promptOccupation";

import IconButton from "util/iconButton";
import { FaCheck, FaTimes } from "react-icons/fa";
import { RadioColumns } from "util/optionColumns";

const { Field, Control } = Form;
dayjs.extend(localeData);
dayjs.extend(objectSupport);

function useOccupationState(currentStatuses, changes) {
  const originalId =
    currentStatuses.length === 1 ? currentStatuses[0].occupation_id : null;
  const selectedId = changes.occupation_id ?? originalId;
  const originalDetail = useOccupationDetail(originalId);
  const selectedDetail = useOccupationDetail(selectedId);

  return { originalId, selectedId, originalDetail, selectedDetail };
}

function OccupationSection({
  occupationState,
  originalStatusRecord,
  changeKeys,
  whose,
}) {
  const dispatch = useDispatch();
  const onChange = (newId) =>
    updateChanges(
      dispatch,
      changeKeys,
      "occupation_id",
      occupationState.originalId,
      newId
    );
  return (
    <Message color="dark">
      <Message.Header>
        <span>Occupation or profession</span>
      </Message.Header>
      <Message.Body>
        {originalStatusRecord.statuses.length > 1 ? (
          <ErrorMessage>
            We currently have more than one occupation listed for you:
            {originalStatusRecord.statuses.map((status, index) => (
              <ShowOriginalStatus
                key={index}
                statusRecord={originalStatusRecord}
                whichStatus={index}
                settings={{ skipLocation: true, skipDepartment: true }}
              />
            ))}
            Please choose the one that is your <em>primary</em> occupation.
          </ErrorMessage>
        ) : null}
        <PromptOccupation {...{ occupationState, onChange }} />
      </Message.Body>
    </Message>
  );
}

function useSetHomeLocation(originalStatusDetails, changeKeys) {
  const dispatch = useDispatch();
  return (location) =>
    updateChanges(
      dispatch,
      changeKeys,
      "location",
      originalStatusDetails.location,
      location
    );
}

function InstitutionSection({
  institutionRequirements,
  changeKeys,
  originalStatusDetails,
  proposedStatusDetails,
  allAffiliations,
  whose,
}) {
  const institutionOptions = usePreloadInstitutionOptions();
  const institutionLookup = useInstitutionLookup();

  const dispatch = useDispatch();

  const equalAffiliations = (a, b) => {
    const equalRecord = (r, s) =>
      AFFILIATION_CHANGEABLE_FIELDS.filter((k) => r[k] !== s[k]).length === 0;

    return (
      a &&
      b &&
      a.length === b.length &&
      a
        .map((record, index) => equalRecord(record, b[index]))
        .filter((result) => !result).length === 0
    );
  };

  // Normally we would remove the affiliations list from the change record
  // altogether if it's the same as the affiliations list in the original
  // detailed status record, but only if the original status record had
  // a unique item in its statuses list. If it had zero or multiple statuses,
  // we always need to submit a non-null value for affiliations.

  const affiliationSubmissionRequired =
    originalStatusDetails.noStatuses || originalStatusDetails.multipleStatuses;

  const setAffiliations = (newList) =>
    updateChanges(
      dispatch,
      changeKeys,
      "affiliations",
      affiliationSubmissionRequired ? null : originalStatusDetails.affiliations,
      newList,
      equalAffiliations
    );

  const updateSingleAffiliationRecord = (newValues, position) => {
    const affiliations = [...proposedStatusDetails.affiliations];

    // Allow an "update" that's really adding a new affiliation to the list:
    if (affiliations[position] === undefined) affiliations[position] = {};

    const differentInstitution =
      newValues.institution_id !== undefined &&
      newValues.institution_id !== affiliations[position].institution_id;

    const baseRecord = differentInstitution ? {} : affiliations[position];
    if (differentInstitution) {
      const previousDetails =
        originalStatusDetails.affiliationDetailsIndex[
          newValues.institution_id
        ] || {};
      const institution = institutionLookup[newValues.institution_id] || {};
      if (previousDetails.department_name !== undefined)
        baseRecord.department_name = previousDetails.department_name;
      if (previousDetails.location !== undefined)
        baseRecord.location = previousDetails.location;
      if (institution.location !== undefined)
        baseRecord.institution_location = institution.location;
    }
    const newAffiliations = affiliations.map((a, i) =>
      i === position ? { ...baseRecord } : { ...a }
    );

    Object.keys(newValues).forEach((key) => {
      if (newValues[key] === undefined) delete newAffiliations[position][key];
      else newAffiliations[position][key] = newValues[key];
    });

    // If institution is optional and the user clears it (or clears the
    // last entry in the list if more than one), remove it from the list
    // altogether. (For mandatory institutions, we leave it present but
    // unspecified, forcing the person to choose another in its place, or
    // to manually remove it if more than one on the list).
    if (
      institutionRequirements.optional &&
      newAffiliations[newAffiliations.length - 1].institution_id === undefined
    )
      newAffiliations.pop();

    setAffiliations(newAffiliations);
  };

  const setHomeLocation = useSetHomeLocation(originalStatusDetails, changeKeys);
  const inconsistencies = proposedStatusDetails.internalInconsistencies;
  useEffect(() => {
    if (inconsistencies.length > 0)
      inconsistencies.forEach(
        ({ affiliationNumber, correction }) =>
          updateSingleAffiliationRecord(correction, affiliationNumber),
        [inconsistencies, updateSingleAffiliationRecord]
      );
  });
  const currentList = proposedStatusDetails.affiliations;
  const originalList = originalStatusDetails.affiliations;
  const adjustedList = institutionRequirements.notReady
    ? null
    : institutionRequirements.notApplicable && currentList.length > 0
    ? []
    : currentList.length === 0 &&
      !institutionRequirements.notApplicable &&
      !institutionRequirements.optional
    ? originalList.length > 0
      ? originalList
      : [{}]
    : affiliationSubmissionRequired && proposedStatusDetails.unchanged
    ? currentList
    : null;

  useEffect(() => {
    if (adjustedList) setAffiliations(adjustedList);
  });

  // If institution is optional, we don't push a blank affiliation
  // record onto the affiliations list if the list is empty (for then
  // it would be mandatory), BUT we do issue one institution prompt even if
  // the affiliations list is empty.

  const numInstitutionPrompts =
    institutionRequirements.optional && currentList.length === 0
      ? 1
      : currentList.length;

  return institutionRequirements.notApplicable ? null : (
    <Message className="institution-select" color="dark">
      <Message.Header>{institutionRequirements.questionWording}</Message.Header>
      <Message.Body>
        <Fragment>
          {!institutionOptions.loaded ? "Please wait ..." : null}
          {Array(numInstitutionPrompts)
            .fill(0)
            .map((_, index) => (
              <AffiliationPrompt
                key={index}
                statusDetails={proposedStatusDetails}
                optional={institutionRequirements.optional}
                {...{
                  index,
                  setAffiliations,
                  updateSingleAffiliationRecord,
                  setHomeLocation,
                  institutionOptions,
                  associatedInstitutions: allAffiliations,
                }}
              />
            ))}
          <AffiliationPrompt
            key="add-new"
            statusDetails={proposedStatusDetails}
            optional={institutionRequirements.optional}
            index={null}
            {...{
              setAffiliations,
              updateSingleAffiliationRecord,
              setHomeLocation,
              institutionOptions,
              associatedInstitutions: allAffiliations,
            }}
          />
        </Fragment>
      </Message.Body>
    </Message>
  );
}

function HomeLocationSection({
  dontRequireAffiliationLocations,
  proposedStatusDetails,
  originalStatusDetails,
  changeKeys,
}) {
  const setHomeLocation = useSetHomeLocation(originalStatusDetails, changeKeys);
  const { locationLookup } = useGeographicalLocations();
  const { authenticatedAsStaff: amStaff } = useAuthenticationState();
  const your = amStaff ? "this person's" : "your";
  const doyou = amStaff ? "does this person" : "do you";

  const homeLocation = proposedStatusDetails.location;
  const affiliationDetailsList = proposedStatusDetails.affiliationDetailsList;
  const completeAffiliationLocationProperties = dontRequireAffiliationLocations
    ? []
    : affiliationDetailsList
        .filter(
          (d) =>
            d.locationDetails &&
            d.locationDetails.properties &&
            !d.conflictingCountry &&
            !d.conflictingArea &&
            !d.locationDetails.properties.missingArea
        )
        .map((d) => d.locationDetails.properties);

  const ready =
    dontRequireAffiliationLocations ||
    (affiliationDetailsList.length > 0 &&
      affiliationDetailsList.length ===
        completeAffiliationLocationProperties.length);

  const locationLabels = Object.fromEntries(
    completeAffiliationLocationProperties.map(({ symbolic_id, full_label }) => [
      symbolic_id,
      full_label,
    ])
  );
  const presetLocationOptions = Array.from(
    new Set(completeAffiliationLocationProperties.map((l) => l.symbolic_id))
  );

  const presetLocationCountries = Array.from(
    new Set(
      completeAffiliationLocationProperties.map((l) => l.country_symbolic_id)
    )
  );

  const singleCountry =
    presetLocationCountries.length === 1
      ? locationLookup[presetLocationCountries[0]]
      : null;

  const initialOtherSelection =
    singleCountry && !presetLocationOptions.includes(singleCountry.symbolic_id)
      ? singleCountry.symbolic_id
      : "";

  const singlePresetOption = presetLocationOptions.length === 1;
  const noPresetOption = presetLocationOptions.length === 0;
  const selectedOption = presetLocationOptions.includes(homeLocation)
    ? homeLocation
    : (homeLocation === undefined || homeLocation === null) &&
      singlePresetOption
    ? presetLocationOptions[0]
    : initialOtherSelection;
  const otherSelected = selectedOption === initialOtherSelection;
  const question = singlePresetOption
    ? `Is ${your} place of residence also in ` +
      locationLabels[presetLocationOptions[0]] +
      `, or ${doyou} work/study remotely from somewhere else?`
    : `Which of the different locations above is ${your} actual ` +
      "place of residence? ";

  if (!ready) return null;
  else
    return (
      <Message className="location-select" color="dark">
        <Message.Header>Place of Residence</Message.Header>
        <Message.Body>
          {noPresetOption ? null : <div className="block">{question}</div>}
          {presetLocationOptions.map((loc) => (
            <Field key={loc}>
              <RadioColumns
                name="home-location"
                value={selectedOption}
                optionValue={loc}
                onChange={setHomeLocation}
              >
                {singlePresetOption ? "Yes, place of residence is " : ""}
                {locationLabels[loc]}
              </RadioColumns>
            </Field>
          ))}
          {noPresetOption ? null : (
            <Field>
              <RadioColumns
                name="home-location"
                value={selectedOption}
                optionValue={initialOtherSelection}
                onChange={setHomeLocation}
              >
                {singlePresetOption
                  ? "No, it is somewhere else"
                  : "somewhere else"}
                {otherSelected ? ":" : ""}
              </RadioColumns>
            </Field>
          )}
          {otherSelected ? (
            <PromptLocation
              location={homeLocation}
              setLocation={setHomeLocation}
              countryPrompt="Country"
            ></PromptLocation>
          ) : null}
        </Message.Body>
      </Message>
    );
}

/* Determine whether anything is incomplete (or in need of correction),
   what messages to display if there are, and if changing from one set
   of complete occupation/affiliation information to another, how to
   summarize that change when asking the user a question about when the
   change should take effect.
*/

function useAssessChanges(
  originalStatusRecord,
  occupationState,
  phdYearState,
  changes
) {
  // Incomplete items are those that must be dealt with before changes can be
  // saved. For staff, this is a smaller list; for example, a staff member
  // editing someone's information might not know their year of PhD, and in
  // this case we allow the staff member to proceed anyway, but the person
  // themselves won't be allowed to proceed without it.

  const incompleteItems = {
    mustResolve: [],
    canLeaveUnresolved: [],
  };

  const { authenticatedAsStaff: amStaff } = useAuthenticationState;
  const whose = amStaff ? "this person's" : "your";

  const EVEN_STAFF_MUST_RESOLVE = true;
  const STAFF_CAN_LEAVE_UNRESOLVED = false;

  const addIncompleteItem = (item, staffMustResolve) =>
    incompleteItems[
      amStaff && !staffMustResolve ? "canLeaveUnresolved" : "mustResolve"
    ].push(item);

  /* How to describe change from one complete occupation/affiliation status
    to another, when requesting an effective date for the change. */

  const originalStatusDetails = useOriginalStatusDetails(originalStatusRecord);

  const proposedStatusDetails = useProposedStatusDetails(
    originalStatusDetails,
    changes
  );

  const statusChangeSummary = {
    whatChanged: null,
    showSettings: {},
    wasIncomplete: false,
    nowIncomplete: false,
    merelyAugmented: false,
  };

  // Assess state of change for Year of PhD (this is a simple field where
  // a change is simply a correction to a missing or erroneous value)

  if (phdYearState.incomplete)
    addIncompleteItem(phdYearState.incomplete, STAFF_CAN_LEAVE_UNRESOLVED);

  // Assess state of change for items related to affiliation/occupation
  // status. These items can have different values for different periods of
  // time, so we prepare a summary of the change to use in a question about
  // when the change should take effect.

  // We distinguish between the case of a change from one complete set of
  // information to another and the case of filling in what was originally
  // missing or incomplete. In the latter case we won't have a  "when
  // did the change happen?" type of prompt. We set
  // statusChangeSummary.wasIncomplete to signify this.

  statusChangeSummary.wasIncomplete =
    !occupationState.originalDetail.occupation || // no occupation
    occupationState.originalDetail.remapped || // old obsolete occupation code
    !originalStatusDetails.haveLocations || // no location information
    originalStatusDetails.haveMissingAreas || // incomplete location information
    originalStatusDetails.haveLocationConflicts ||
    originalStatusDetails.haveAmbiguousLocations;

  if (occupationState.selectedId === null) {
    addIncompleteItem(
      `Please specify ${whose} occupational status`,
      statusChangeSummary.wasIncomplete
        ? STAFF_CAN_LEAVE_UNRESOLVED
        : EVEN_STAFF_MUST_RESOLVE
    );
    statusChangeSummary.nowIncomplete = true;
  }

  proposedStatusDetails.incompleteItems.forEach((item) => {
    addIncompleteItem(
      item.message,
      item.staffCanLeaveUnresolved
        ? STAFF_CAN_LEAVE_UNRESOLVED
        : EVEN_STAFF_MUST_RESOLVE
    );
    statusChangeSummary.nowIncomplete = true;
  });

  const assessment = {
    whose,
    incompleteItems,
    statusChangeSummary,
    originalStatusDetails,
    proposedStatusDetails,
  };

  if (statusChangeSummary.wasIncomplete || statusChangeSummary.nowIncomplete)
    return assessment;

  /* Remaining code is only for the case when there is a completely
    specified status both before and after the change (if any). */

  const haveChangedOccupation =
    proposedStatusDetails.occupation_id !== undefined &&
    proposedStatusDetails.occupation_id !== originalStatusDetails.occupation_id;

  const haveChangedInstitutions =
    proposedStatusDetails.affiliationDetailsList.length !==
      originalStatusDetails.affiliationDetailsList.length ||
    proposedStatusDetails.affiliationDetailsList.filter(
      (d, i) => d.id !== originalStatusDetails.affiliationDetailsList[i].id
    ).length > 0;

  const departmentChanges = proposedStatusDetails.affiliationDetailsList
    .map((affil) => {
      const id = affil.id;
      const origInfo = affil.original;
      if (!origInfo) return {}; // new institution

      const origDept = origInfo.department_name;
      return origDept !== null && affil.department_name !== origDept
        ? { id, changed: true }
        : origDept == null && affil.department_name !== null
        ? { id, added: true }
        : {}; // unchanged department
    })
    .filter((c) => c.changed || c.added);

  const haveChangedDepartments =
    departmentChanges.filter((c) => c.changed).length > 0;
  const haveAddedDepartments =
    departmentChanges.filter((c) => c.added).length > 0;

  const institutionLocationChanges = proposedStatusDetails.affiliationDetailsList
    .map((updatedInfo) => {
      const { id, name } = updatedInfo;
      const origInfo = originalStatusDetails.affiliationDetailsIndex[id];

      if (!origInfo) return {}; // new institution

      const origLocation = origInfo.locationDetails.properties;
      const newLocation = updatedInfo.locationDetails.properties;
      if (!origLocation) return newLocation ? { id, name, added: true } : {};

      if (!newLocation) return { id, name, changed: true };
      return newLocation.symbolic_id === origLocation.symbolic_id
        ? {}
        : origLocation.isCountry &&
          newLocation.country_id === origLocation.country_id
        ? { id, name, added: true }
        : { id, name, changed: true };
    })
    .filter((c) => c.changed || c.added);

  const newHomeLoc = proposedStatusDetails.homeLocationDetails.properties;
  const origHomeLoc = originalStatusDetails.homeLocationDetails.properties;
  const addedHomeArea =
    newHomeLoc &&
    origHomeLoc &&
    newHomeLoc.country_id === origHomeLoc.country_id &&
    origHomeLoc.isCountry &&
    !newHomeLoc.isCountry;

  const haveAddedHomeLocation = newHomeLoc && (!origHomeLoc || addedHomeArea);

  const haveChangedHomeLocation =
    newHomeLoc &&
    origHomeLoc &&
    newHomeLoc.symbolic_id !== origHomeLoc.symbolic_id &&
    !haveAddedHomeLocation;

  const haveChangedInstitutionLocations =
    institutionLocationChanges.filter((c) => c.changed).length > 0;

  const haveChangedLocations =
    haveChangedHomeLocation || haveChangedInstitutionLocations;

  const haveAddedLocations =
    haveAddedHomeLocation ||
    institutionLocationChanges.filter((c) => c.added).length > 0;

  const singleInstitutionChange = changes.location
    ? null
    : departmentChanges.length > 1
    ? null
    : institutionLocationChanges.length > 1
    ? null
    : departmentChanges.length === 1
    ? institutionLocationChanges.length === 0 ||
      institutionLocationChanges[0].id === departmentChanges[0].id
      ? departmentChanges[0].id
      : null
    : institutionLocationChanges.length === 0
    ? null
    : institutionLocationChanges[0];

  // If the only changes were adding additional optional information, treat
  // it as if original information was incomplete: don't prompt for date
  // for changes.

  if (
    !haveChangedOccupation &&
    !haveChangedInstitutions &&
    !haveChangedDepartments &&
    !haveChangedLocations
  ) {
    statusChangeSummary.merelyAugmented = true;
    return assessment;
  }

  const categoriesChanged = [];
  if (haveChangedOccupation) categoriesChanged.push("occupation");
  if (haveChangedInstitutions) categoriesChanged.push("institution");
  if (haveChangedDepartments && !haveChangedInstitutions)
    categoriesChanged.push("department");
  if (
    haveChangedHomeLocation ||
    (haveChangedInstitutionLocations && !haveChangedInstitutions)
  )
    categoriesChanged.push("location");

  statusChangeSummary.whatChanged = categoriesChanged.join("/");
  const settings = statusChangeSummary.showSettings;

  settings.skipOccupation = !haveChangedOccupation;

  settings.skipInstitutions =
    !haveChangedInstitutions &&
    !haveChangedDepartments &&
    !haveChangedInstitutionLocations;

  if (
    singleInstitutionChange &&
    !haveChangedInstitutions &&
    !haveChangedOccupation
  ) {
    settings.singleInstitution = singleInstitutionChange.id;
    if (changes.affiliations.length > 1)
      statusChangeSummary.whatChanged =
        statusChangeSummary.whatChanged + " at " + singleInstitutionChange.name;
  }

  settings.skipLocation =
    !haveChangedLocations &&
    !(haveAddedLocations && (haveChangedOccupation || haveChangedInstitutions));
  settings.skipDepartment =
    !haveChangedDepartments &&
    !(
      haveAddedDepartments &&
      (haveChangedOccupation || haveChangedInstitutions)
    );
  settings.showNoDepartment = haveAddedDepartments && !settings.skipDepartment;

  return assessment;
}

function EditAffiliationStatus({ dateSpecifier, forRegistration }) {
  const { id, data: profileData } = useSelector(profileSlice.selector);
  const requestedCurrent = dateSpecifier === "indefinite-future";

  const dispatch = useDispatch();
  const doReturn = useReturn();

  const [closeAfterSaving, setCloseAfterSaving] = useState(false);

  const doClose = useCallback(() => {
    dispatch(profileSlice.actions.setAffiliationIncomplete(null));
    if (requestedCurrent)
      dispatch(profileSlice.actions.setAffiliationReviewed(true));
    doReturn();
    // Prevent any subsequent automatic call to doClose (that might
    // result in a double-pop of the history stack).
    setCloseAfterSaving(false);
  }, [dispatch, requestedCurrent, doReturn]);

  /* Unqualified "change" references here, such as changeRecord, changeKeys,
     and changes, refer to affiliation status record changes. It is
     convenient to prompt for PhD Year here as well, but that's a property
     of the person not the affiliation, so its changeRecord, changeKeys, and
     changes are inside the phdYearState object below. */

  const [changeRecord, changeKeys] = useAffiliationChangeInformation(
    id,
    dateSpecifier
  );
  const changes = changeRecord ? changeRecord.changes : {};

  /* The affiliation status record in effect on the specified date,
    before any changes are applied */

  const [originalStatusRecord] = getStatusSummary(profileData).filter(
    (r) => r.through === (requestedCurrent ? null : dateSpecifier)
  );
  const originalStatuses = originalStatusRecord
    ? originalStatusRecord.statuses
    : [];
  useEffect(() => {
    if (!originalStatusRecord) doClose();
  });

  /* Assess occupation state, based on original status record as modified
    by any changes that have been selected. */
  const occupationState = useOccupationState(originalStatuses, changes);
  const occupationDetail = occupationState.selectedDetail;

  /* Assess institution state */

  const institutionRequirements = {
    notReady: !occupationDetail.occupation,
    notApplicable:
      !occupationDetail.occupation || occupationDetail.noInstitution,
    optional: occupationDetail.optionalInstitution,
    questionWording: occupationDetail.institutionQuestion ?? null,
  };

  /* Assess state of PhD year entry, if required by selected occupation */
  const phdYearState = usePhdYearState(id, "affiliation", profileData, [
    occupationDetail,
  ]);

  /* Analyze all these states, to get information about all the changes
     that have been requested, what errors or missing items there are,
     and how the changes can be summarized for asking the user about
     when they should take effect. */

  const {
    incompleteItems,
    statusChangeSummary,
    originalStatusDetails,
    proposedStatusDetails,
    whose,
  } = useAssessChanges(
    originalStatusRecord,
    occupationState,
    phdYearState,
    changes
  );

  const datesSectionPreparation = usePrepareDatesSection({
    originalStatusRecord,
    wasIncomplete: statusChangeSummary.wasIncomplete,
    merelyAugmented: statusChangeSummary.merelyAugmented,
    changes,
    changeKeys,
  });

  /* Save / cancel actions */

  const savingAffiliationStatus = changeRecord ? changeRecord.pending : false;
  const savingPhdYear = phdYearState.changeRecord
    ? phdYearState.changeRecord.pending
    : false;
  const saving = savingAffiliationStatus || savingPhdYear;

  const failedAffiliationStatus = changeRecord ? changeRecord.failed : null;
  const failedPhDYear = phdYearState.changeRecord
    ? phdYearState.changeRecord.failed
    : null;
  const failed = failedAffiliationStatus || failedPhDYear;

  const haveChanges = changeRecord || phdYearState.changeRecord;

  const doCancel = useCallback(() => {
    dispatch(changesSlice.actions.deleteChangeRecord(changeKeys));
    dispatch(changesSlice.actions.deleteChangeRecord(phdYearState.changeKeys));
    doClose();
  }, [changeKeys, phdYearState.changeKeys, dispatch, doClose]);

  const haveChangeRecord = !!changeRecord;
  const havePhdYearChangeRecord = !!phdYearState.changeRecord;

  const doSave = useCallback(() => {
    if (haveChangeRecord) dispatch(changesSlice.asyncActions.save(changeKeys));
    if (havePhdYearChangeRecord)
      dispatch(changesSlice.asyncActions.save(phdYearState.changeKeys));
    setCloseAfterSaving(true);
  }, [
    haveChangeRecord,
    havePhdYearChangeRecord,
    dispatch,
    changeKeys,
    phdYearState.changeKeys,
  ]);

  useEffect(() => {
    if (closeAfterSaving && failed) setCloseAfterSaving(false);
    if (closeAfterSaving && !changeRecord && !phdYearState.changeRecord)
      doClose();
  }, [
    closeAfterSaving,
    failed,
    changeRecord,
    phdYearState.changeRecord,
    doClose,
  ]);

  const dontRequireAffiliationLocations =
    institutionRequirements.notApplicable ||
    (institutionRequirements.optional &&
      proposedStatusDetails.affiliations.length === 0);

  /* Main Prompt */

  // Normally we prompt for PhD year only when the occupation is faculty
  // or postdoc, in which case we will also be prompting for institution;
  // however, if occupation is something else (e.g. emeritus) there might
  // still be a PhD year recorded for the person, in which case we do
  // want to have a prompt that shows it.

  if (!originalStatusRecord)
    return <div>Invalid date specifier - returning to main page.</div>;

  const incomplete = incompleteItems.mustResolve.length > 0;
  return (
    <div>
      <p className="is-italic block">
        This information helps us understand the academic background of
        participants in Fields Institute activities
      </p>
      <OccupationSection
        {...{
          occupationState,
          originalStatusRecord,
          changeKeys,
          whose,
        }}
      />
      <InstitutionSection
        {...{
          institutionRequirements,
          originalStatusDetails,
          proposedStatusDetails,
          allAffiliations: profileData.affiliation_summary,
          changeKeys,
          whose,
        }}
      />

      <HomeLocationSection
        {...{
          dontRequireAffiliationLocations,
          proposedStatusDetails,
          originalStatusDetails,
          changeKeys,
        }}
      />

      {phdYearState.promptProps.needed ? (
        <Message color="dark">
          <Message.Header>Academic career stage</Message.Header>
          <Message.Body>{phdYearState.promptProps.heading}</Message.Body>

          <Field>
            <Control>
              <PromptPhdYear promptProps={phdYearState.promptProps} />
            </Control>
          </Field>
        </Message>
      ) : null}
      {!datesSectionPreparation ? null : (
        <div className="affiliation-date-selection">
          <div className="affiliation-section">
            {datesSectionPreparation.defer ? (
              <Fragment>
                The information you provide here will be used to supplement
                missing information in our database, for the entire period from{" "}
                {originalStatusRecord.showFrom} through
                {originalStatusRecord.showThrough}. After that you'll have the
                opportunity to specify different information for different
                times, if applicable.
              </Fragment>
            ) : datesSectionPreparation.omit ? (
              <Fragment>
                If you make changes above to information that's already in our
                database, you'll be asked to choose what date range the changes
                apply to. Currently however you've only added new information,
                so it will be entered for the entire period from{" "}
                {originalStatusRecord.showFrom} through{" "}
                {originalStatusRecord.showThrough}.
              </Fragment>
            ) : (
              <DatesSection
                {...{
                  originalStatusRecord,
                  originalStatusDetails,
                  proposedStatusDetails,
                  statusChangeSummary,
                  whose,
                }}
                preparation={datesSectionPreparation}
              />
            )}
          </div>
        </div>
      )}

      <div className="mt-4">
        {incompleteItems.mustResolve.length > 0 ? (
          <Content>
            <ul>
              {incompleteItems.mustResolve.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </Content>
        ) : null}
        {failed ? (
          <ErrorMessage header="Unable to Save Changes">
            {failedAffiliationStatus ? failedAffiliationStatus.message : null}
            {failedPhDYear ? failedPhDYear.message : null}
          </ErrorMessage>
        ) : null}
        {saving && !failed ? (
          <div className="saving">Saving; please wait</div>
        ) : forRegistration ? (
          <div className="buttons">
            <IconButton icon={FaCheck} onClick={incomplete ? () => {} : doSave}>
              Save and Continue Registration
            </IconButton>
            {haveChanges ? (
              <IconButton icon={FaTimes} onClick={doCancel}>
                Cancel Changes
              </IconButton>
            ) : null}
          </div>
        ) : (
          <div className="buttons">
            {haveChanges && !incomplete ? (
              <IconButton icon={FaCheck} onClick={doSave}>
                Save Changes
              </IconButton>
            ) : null}
            <IconButton icon={FaTimes} onClick={doCancel}>
              {haveChanges ? "Cancel Changes" : "Close"}
            </IconButton>
          </div>
        )}
      </div>
    </div>
  );
}

export default EditAffiliationStatus;
